import axios from 'axios';
import difference from 'lodash/difference';
import flatten from 'lodash/flatten';
import { loadFiles, log } from 'loader-base';
import { getBasePath, getBundleFiles } from './bundle-helper';
import { getBowerDependencies } from './component-helper';
import { dashesToCamel, camelToDashes } from './util';

let loadedFiles;
let validBundles;

function loadWithSelfCDN(componentSource, lazy, resolvedDependencies, additionalComponents, css) {
    return new Promise((resolve) => {
        let dependencyList = [];
        const failedToLoad = [];
        const bundleFileOverrides = componentSource.overrides || {};

        let fileList;

        Object.keys(resolvedDependencies).forEach((name) => {
            if ((!lazy.bundlesToDownload
                || name === 'angular'
                || lazy.bundlesToDownload[dashesToCamel(name)]
                || lazy.bundlesToDownload[camelToDashes(name)])
                && additionalComponents.indexOf(camelToDashes(name)) === -1
                && additionalComponents.indexOf(dashesToCamel(name)) === -1) {
                const bundleFiles = bundleFileOverrides[dashesToCamel(name)] || bundleFileOverrides[camelToDashes(name)];
                const { version } = resolvedDependencies[name];
                // firstly use override bundles
                // secondly use the client special bundles
                // finally use the default version bundles
                if (bundleFiles && bundleFiles.length) {
                    fileList = bundleFiles;
                } else if (version) {
                    fileList = getBundleFiles(componentSource.environment, name, version, resolvedDependencies[name].instid, componentSource.type);
                } else {
                    fileList = [];
                    failedToLoad.push(name);
                    log.error(`Cannot find the bundle files for component: ${name}`);
                }
                // make sure angular is in the first one of the load list
                if (name === 'angular') {
                    dependencyList.unshift(fileList);
                } else {
                    dependencyList.push(fileList);
                }
            }
        });

        dependencyList = flatten(dependencyList);

        // filter the load list with loaded list
        if (!lazy || !lazy.forceRedownload) {
            dependencyList = difference(dependencyList, Array.from(loadedFiles));
        }

        const fileTypeFilter = css.includeComponentCss ? null : ['css'];

        loadFiles(dependencyList, fileTypeFilter).then((loadedList) => {
            Object.keys(loadedList).forEach((file) => {
                loadedFiles.add(file);
            });
            resolve({ resolutions: dependencyList, failedToLoad });
        });
    });
}

function loadComponentIndexHandler(loaderConfig) {
    return new Promise((resolve) => {
        const resolvedDependencies = getBowerDependencies(loaderConfig);
        loaderConfig.validBundles = loaderConfig.validBundles || {};
        loadWithSelfCDN(loaderConfig.componentSource, loaderConfig.lazy, resolvedDependencies, loaderConfig.additionalComponents, loaderConfig.css)
            .then(resolutionsAndFailed => resolve({
                resolutions: resolutionsAndFailed.resolutions,
                failedToLoad: resolutionsAndFailed.failedToLoad,
                resolvedDependencies,
            }));
    });
}

/**
 * Retrieves a list of available bundles (with versions) and downloads the bundles specified in the loader configuration.
 * @param {*} loaderConfig
 * @param {set} loadedList - A set with all previously loaded files on the page
 * @returns {Promise} - Resolves with a manifest containing the loader configuration, app config and a list of the loaded files.
 */
const bundlesReady = function bundlesReady(loaderConfig, loadedList) {
    return new Promise((resolve, reject) => {
        loadedFiles = loadedList || new Set();
        let promise;
        if (loaderConfig.componentSource.useClientSpecificBundle) {
            promise = Promise.resolve({
                data: {},
            });
        } else if (validBundles) {
            promise = Promise.resolve({
                data: validBundles,
            });
        } else {
            promise = axios({
                method: 'get',
                url: `${getBasePath(loaderConfig.componentSource.environment, loaderConfig.componentSource.type)}/components.json?_=${new Date().getTime()}`,
            });
        }
        promise.then((res) => {
            validBundles = res.data;
            loaderConfig.validBundles = validBundles;
            loadComponentIndexHandler(loaderConfig).then(resolutionsAndResolvedDeps => resolve({
                resolutions: resolutionsAndResolvedDeps.resolutions,
                resolvedDependencies: resolutionsAndResolvedDeps.resolvedDependencies,
                loaderConfig,
                config: loaderConfig.config,
                failedToLoad: resolutionsAndResolvedDeps.failedToLoad,
            }));
        }).catch(() => {
            reject(loaderConfig);
        });
    });
};

const handleClientSpecificBundle = loaderConfig => new Promise((resolve, reject) => {
    if (loaderConfig.componentSource && loaderConfig.componentSource.useClientSpecificBundle) {
        const { namespace } = loaderConfig.configuration;
        if (!namespace) {
            reject(new Error('Missing configuration.namespace (mandatory when using "useClientSpecificBundle")'));
            return;
        }

        let { environment } = loaderConfig.componentSource || 'prod';
        const firstComponentInstance = Object.keys(loaderConfig.config.components)[0];
        const firstComponentType = loaderConfig.config.components[firstComponentInstance].type;
        const namespacePath = namespace.replace(/\./g, '/');
        environment = environment !== 'prod' ? `-${environment}` : '';
        let version = loaderConfig.componentSource.clientSpecificBundleVersion;
        let promiseChain = Promise.resolve();
        if (!version) {
            promiseChain = promiseChain.then(() => axios({
                method: 'get',
                url: `http://mwc-cdn${environment}.morningstar.com/client-bundles/${namespacePath}/${namespace}-bundle.json`,
            })).then((res) => {
                version = res.data ? res.data.revision : undefined;
            }).catch(() => reject(new Error('Failed to retrieve metadata file from CDN.')));
        }
        promiseChain.then(() => {
            if (version) {
                loaderConfig.componentSource.overrides = {
                    [firstComponentType]: [
                        `http://mwc-cdn${environment}.morningstar.com/client-bundles/${namespacePath}/${version}/${namespace}-bundle.js`,
                        `http://mwc-cdn${environment}.morningstar.com/client-bundles/${namespacePath}/${version}/${namespace}-bundle.css`,
                    ],
                };
                resolve();
            } else {
                reject(new Error('Metadata file did not contain version/revision number.'));
            }
        });
    } else {
        resolve();
    }
});

const resetValidBundles = () => {
    validBundles = undefined;
};

export { bundlesReady, handleClientSpecificBundle, loadWithSelfCDN, resetValidBundles };



// WEBPACK FOOTER //
// ./src/bundle-loader.js